/**
 * Marketplace sync strategy service.
 *
 * Given a marketplace code from the Marketplaces API (e.g. "AMAZON", "SHOPIFY"),
 * returns a strategy that tells the product type sync flow which backend
 * provider and queries to use.
 *
 * Only providers with real backend implementations are enumerated.
 * All other marketplace codes (NOON, …) fall through to
 * 'unsupported' automatically — no need to list them here.
 */

/**
 * Implemented backend providers.
 * 'unsupported' covers any marketplace code not yet integrated.
 */
export type SyncProvider =
  | "amazon"
  | "shopify"
  | "meesho"
  | "coupang"
  | "ebay"
  | "snapdeal"
  | "category-table"
  | "unsupported";

export interface MarketplaceSyncStrategy {
  /** Which GraphQL queries / backend client to use. */
  provider: SyncProvider;
  /** The `marketplace` string sent to startProductTypeSync. Derived from the marketplace code. */
  syncMarketplace: string;
  /** Whether this marketplace supports API-based product type search and sync. */
  supportsSync: boolean;
}

/**
 * Only marketplaces with a real backend client implementation need an entry here.
 * Every other code from the Marketplaces API will fall through to the 'unsupported' default.
 */
const IMPLEMENTED_PROVIDERS: Record<
  string,
  Pick<MarketplaceSyncStrategy, "provider" | "supportsSync">
> = {
  AMAZON: { provider: "amazon", supportsSync: true },
  SHOPIFY: { provider: "shopify", supportsSync: true },
  MEESHO: { provider: "meesho", supportsSync: true },
  COUPANG: { provider: "coupang", supportsSync: true },
  EBAY: { provider: "ebay", supportsSync: true },
  SNAPDEAL: { provider: "snapdeal", supportsSync: true },
  FLIPKART: { provider: "category-table", supportsSync: true },
  SHOPCLUES: { provider: "category-table", supportsSync: true },
};

/**
 * Returns the sync strategy for a marketplace code from the Marketplaces API.
 * Unknown codes (new platforms not yet integrated) return 'unsupported'.
 */
export function getMarketplaceSyncStrategy(
  marketplaceCode: string,
): MarketplaceSyncStrategy {
  const key = marketplaceCode.toUpperCase();
  const impl = IMPLEMENTED_PROVIDERS[key];
  return {
    syncMarketplace: marketplaceCode.toLowerCase(),
    supportsSync: false,
    provider: "unsupported",
    ...impl,
  };
}
